import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const Products = () => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [loading, setLoading] = useState(true);
  const [visibleSections, setVisibleSections] = useState({});

  // Scroll animation observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setVisibleSections((prev) => ({
            ...prev,
            [entry.target.id]: entry.isIntersecting,
          }));
        });
      },
      { threshold: 0.15, rootMargin: '-80px' }
    );

    const sections = document.querySelectorAll('.animate-section');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, [loading]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get('/api/products');
        setProducts(response.data.data);
        
        // Extract unique categories
        const cats = [...new Set(response.data.data.map(p => p.category))];
        setCategories(['All', ...cats]);
      } catch (error) {
        console.error('Error fetching products:', error);
        // Use static data as fallback
        setProducts(staticProducts);
        const cats = [...new Set(staticProducts.map(p => p.category))];
        setCategories(['All', ...cats]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const staticProducts = [
    { id: 1, name: 'Ductless Laminar Air Flow System', slug: 'ductless-laminar-air-flow-system', shortDescription: 'Superior levels of patient protection through filtered and sterile air tent on operation table.', category: 'Air Quality Systems', featured: true },
    { id: 2, name: 'Ozone Generator (O3 GEN)', slug: 'ozone-generator', shortDescription: 'The Magical Air Disinfectant without any chemicals. Kills bacteria, fungus, virus molds.', category: 'Disinfection Systems', featured: true },
    { id: 3, name: 'Negative Ionizer', slug: 'negative-ionizer', shortDescription: 'Air purification system that improves air quality and health.', category: 'Air Quality Systems', featured: true },
    { id: 4, name: 'PSA Oxygen Generation Plant', slug: 'psa-oxygen-generation-plant', shortDescription: 'On-site Oxygen Generation using PSA technology.', category: 'Oxygen Systems', featured: true },
    { id: 5, name: 'Modular Operation Theatre', slug: 'modular-operation-theatre', shortDescription: 'Complete Modular Operation Theatre system for healthcare facilities.', category: 'Operation Theatre', featured: true },
    { id: 6, name: 'Air Pollution Control HUB', slug: 'air-pollution-control-hub', shortDescription: 'For public places with high footfall.', category: 'Air Quality Systems', featured: false },
    { id: 7, name: 'Isolation Ward', slug: 'isolation-ward', shortDescription: 'Advanced isolation wards with air purification and filtration.', category: 'Isolation Systems', featured: true },
    { id: 8, name: 'Ductless Air Handling Unit', slug: 'ductless-air-handling-unit', shortDescription: 'Maintain positive pressure and sterile air in Operation Theatre.', category: 'Air Handling', featured: false },
    { id: 9, name: 'SS Pass Box', slug: 'ss-pass-box', shortDescription: 'Stainless steel pass box for operation theaters.', category: 'Operation Theatre', featured: false },
    { id: 10, name: 'Negative Pressure System', slug: 'negative-pressure-system', shortDescription: 'COVID-19 response - Protect medical staff from nosocomial infection.', category: 'COVID-19 Solutions', featured: true, special: true },
    { id: 11, name: 'Laminar Air Flow', slug: 'laminar-air-flow', shortDescription: 'HEPA filtered laminar air flow for Operation Theatres.', category: 'Air Quality Systems', featured: false },
  ];

  const displayProducts = products.length > 0 ? products : staticProducts;

  const filteredProducts = selectedCategory === 'All' 
    ? displayProducts 
    : displayProducts.filter(p => p.category === selectedCategory);

  const icons = {
    'Air Quality Systems': '💨',
    'Disinfection Systems': '🦠',
    'Oxygen Systems': '⚗️',
    'Operation Theatre': '🏥',
    'Isolation Systems': '🛡️',
    'Air Handling': '🌀',
    'COVID-19 Solutions': '🔬'
  };

  return (
    <div className="products-page" style={{marginTop: '70px'}}>
      {/* Hero Section */}
      <section className="hero" style={{minHeight: '50vh'}}>
        <div className="hero-content">
          <p className="hero-subtitle">{t('products.badge')}</p>
          <h1 className="hero-title">{t('products.pageTitle')}</h1>
          <p className="hero-description">
            {t('products.pageDescription')}
          </p>
        </div>
      </section>

      {/* Products Section */}
      <section id="products-list" className="section animate-section">
        <div className="container">
          {/* Category Filter */}
          <div 
            className="gallery-tabs"
            style={{
              opacity: visibleSections['products-list'] ? 1 : 0,
              transform: visibleSections['products-list'] ? 'translateY(0)' : 'translateY(40px)',
              transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            {categories.map((category) => (
              <button
                key={category}
                className={`gallery-tab ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="loading">
              <div className="spinner"></div>
            </div>
          ) : (
            <div className="products-grid">
              {filteredProducts.map((product) => (
                <Link to={`/products/${product.slug}`} key={product.id} className="product-card">
                  <div className="product-card-image">
                    {product.image ? (
                      <img 
                        src={product.image} 
                        alt={product.name} 
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: 'var(--radius-md)'
                        }}
                      />
                    ) : (
                      <span className="product-icon">{icons[product.category] || '🏥'}</span>
                    )}
                    {product.special && (
                      <span style={{
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        background: 'var(--accent-color)',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '0.75rem'
                      }}>
                        COVID-19 Solution
                      </span>
                    )}
                  </div>
                  <div className="product-card-content">
                    <span className="product-card-category">{product.category}</span>
                    <h3 className="product-card-title">{product.name}</h3>
                    <p className="product-card-description">{product.shortDescription}</p>
                    <span className="btn btn-secondary" style={{marginTop: 'var(--spacing-md)'}}>
                      Learn More →
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section id="cta" className="cta-section animate-section">
        <div 
          className="container"
          style={{
            opacity: visibleSections.cta ? 1 : 0,
            transform: visibleSections.cta ? 'translateY(0)' : 'translateY(40px)',
            transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          <h2 className="cta-title">{t('products.needHelp.title')}</h2>
          <p className="cta-description">
            {t('products.needHelp.description')}
          </p>
          <Link to="/contact" className="btn btn-white">{t('products.needHelp.contactTeam')}</Link>
        </div>
      </section>
    </div>
  );
};

export default Products;

